import { formatDate } from '@angular/common';
import { Component, effect, EffectRef, inject, Injector, linkedSignal, OnDestroy, OnInit, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AddTicketDialogComponent } from '../add-ticket-dialog/add-ticket-dialog.component';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { ErrorWarningDialogComponent } from '../error-warning-dialog/error-warning-dialog.component';
import { PermitNumDialogComponent } from '../permit-num-dialog/permit-num-dialog.component';
import { SetSelectionDialogComponent } from '../set-selection-dialog/set-selection-dialog.component';
import { CommonFunctionService } from '../shared/common-function-service.service';
import { FishTicket } from '../shared/delivery-fish-ticket.model';
import { FishingEvent } from '../shared/fishing-event.model';
import { GroundfishDelivery } from '../shared/groundfish-delivery.model';
import { Fishery, LocalStorageService } from '../shared/local-storage.service';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { SetErrorBlock } from '../shared/set-error-block.model';
import { TripValidator, lookupItemValidator, vesselDataValidator } from '../shared/trip-validator.model';
import { SubmitDialogComponent } from '../submit-dialog/submit-dialog.component';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { VesselData } from '../vessel-reg/vessel-data.model';
import { Insights } from '../shared/insights.service';


enum DeliveryMode { Current, Completed, EditCompleted};

const EDIT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const EM_EDIT_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * EM trips get a 90-day edit window.
 * All other trips keep the 7-day window measured from receivedDate.
 */
function editWindowExpiry(landing: GroundfishDelivery): Date {
  return new Date(landing.receivedDate.valueOf() + (landing.isEMTrip ? EM_EDIT_WINDOW_MS : EDIT_WINDOW_MS));
}
/**
 * The current delivery page. User will add their sets and finish the delivery here.
 * This component is used to display current delivery and when viewing any completed delivery.
 */
@Component({
    selector: 'app-delivery-detail',
    templateUrl: './delivery-detail.component.html',
    styleUrls: ['./delivery-detail.component.css'],
    standalone: false
})
export class DeliveryDetailComponent implements OnInit, OnDestroy {
  private injector = inject(Injector);
  
  updateEffect!: EffectRef;
  /**
   * Determines the view which can be: 0 = The current unsubmitted delivery, 1 = A completed delivery, 2 = A completed delivery being edited
   */
  viewMode: DeliveryMode = DeliveryMode.Current;
  
  /**
   * If viewing a completed delivery, index into {@link LocalStorageService.received_landings}
   */
  completedInd: number = 0;

  /**
   * When viewing a completed delivery, it can be edited and resubmitted within 7 days of its original submission date
   */
  canEdit: boolean = false;

  /**
   * The current landing/delivery
   */
  landing: GroundfishDelivery = new GroundfishDelivery();
  fishTicketColumns: string[] = ['Buyer', 'Fish Ticket Number', 'Date'];
  @ViewChild(MatTable) table!: MatTable<FishTicket>;

  previousSetIndex: number = 0;
  matToolTipText: string = "Back to Home"

  /**
   * Use a separate control for permit number
   */
  permitNumControl = new FormControl('', [Validators.required]);
  landingForm: FormGroup = new FormGroup({
    vessel: new FormControl(new VesselData('', ''), [Validators.required, vesselDataValidator()]), //Default to last vessel entered
    operator: new FormControl('',  [Validators.required]), //Default to account name
    deliverDate: new FormControl( [Validators.required]), //Default to today's date
    landingPort: new FormControl(new LookupItem(''), [Validators.required, lookupItemValidator()]), //Default to last port entered
    permitType: new FormControl(new LookupItem(''), [Validators.required, lookupItemValidator()]), //Default to last one entered
    isExemptedFP: new FormControl(false), //Default to last
    isEMTrip: new FormControl(false), //Default to last
    isObservedTrip: new FormControl(false) //Same here supposedly
  });

  /**
   * For populating the vessel dropdown
   */
  vesselOptions: WritableSignal<VesselData[]>;

  /**
   * On every change to the landing form, this subscription catches it and runs the form update procedure
   */
  landingFormSubscription: Subscription = new Subscription();

  /**
   * For when the permit number dropdown value changes
   */
  permitNumSubscription: Subscription = new Subscription();
  /**
   * For populating the permit number dropdown
   */

  portOptions: LookupItem[] = [];
  filteredPorts!: Observable<LookupItem[]>;
  portSubscription!: Subscription;

  permitTypeOptions: LookupItem[] = [];
  permitSubscription!: Subscription;

  lastBuyer: string = '';

  /**
   * Periodically updated from {@link TripValidator.errorWarningDialogContent}
   */
  errors: SetErrorBlock[] = [];
  onlyWarnings: boolean = false;
  /**
   * Which attached sets have errors?
   */
  validSets: Map<string, boolean> = new Map<string, boolean>();

  /**
   * When editing a completed delivery, we use this to keep track of which sets were originally submitted with the delivery
   */
  preExistingSets: Map<string, boolean> = new Map<string, boolean>();

  /**
   * The sets on this delivery, sorted
   */
  sortedSets: FishingEvent[] = [];

  landingSubscription!: Subscription;

  previousScreen: string = 'menu';

  constructor(
    public dataSync: LocalStorageService,
    private tripValidator: TripValidator,
    public router: Router,
    private route: ActivatedRoute,
    private onlineStatusService: OnlineStatusService,
    private toastr: ToastrService, 
    private insights: Insights,
    public dialog: MatDialog, 
    private lookupService: LookupDataService,
    private common: CommonFunctionService) { 
      this.vesselOptions = linkedSignal(() => dataSync.current_user().operatedVessels);

      //Completed delivery or not, passed via URL
      this.viewMode = +this.route.snapshot.paramMap.get('pending')!;

      if (this.viewMode == DeliveryMode.Current) {
        this.updateEffect = effect(() => {
          this.landing = this.dataSync.user_trip().groundfishDelivery;
          this.patchForm(false);
          this.sortSetsByDate();
          this.table?.renderRows();
        });
      } else if (this.viewMode == DeliveryMode.EditCompleted) {
        this.updateEffect = effect(() => {
          this.landing = this.dataSync.edited_groundfish();
          this.patchForm(false);
          this.sortSetsByDate();
          this.table?.renderRows();
        })
      }
  }

  collectQueryParameters() {
    this.previousScreen = this.route.snapshot.queryParams.previous ?? 'menu';
    this.previousSetIndex = Number(this.route.snapshot.queryParams['setIndex'] ?? 0);
    this.completedInd = this.viewMode != DeliveryMode.Current ? Number(this.route.snapshot.paramMap.get('index') ?? -1) : -1;
  }

  /**
   * Initialization -- Are we viewing a completed delivery or the current unsubmitted one?
   * Populate the form fields if applicable using {@link LocalStorageService.pending_landing}.
   * If this is a new delivery we fill in some defaults. Set the dropdowns' values and set the form update.
   * All interactivity is disabled if viewing a completed delivery.
   */
  ngOnInit(): void {
    const activeTrip = this.dataSync.user_trip();

    this.insights.trackEvent('Entering delivery', `Initializing delivery details. Open sets ${activeTrip.sets.length}. Attached sets: ${activeTrip.groundfishDelivery.sets.length}`);

    //Set the right tooltip depending on where the back button will take user
    this.matToolTipText = this.previousScreen == 'set_detail' ? 'Back to Set' : this.viewMode != 0 ? 'Back to Deliveries' : '';
    
    this.collectQueryParameters();

    switch (this.viewMode) {
      case DeliveryMode.Current:
        this.landing = activeTrip.groundfishDelivery;
        if (this.landing.isNewDelivery) {
          this.insights.trackEvent('New delivery', 'Initializing new delivery');
          let last = this.dataSync.received_logbooks().find(lb => lb instanceof GroundfishDelivery) as GroundfishDelivery;
          if (last) {
            this.landing.vessel = VesselData.transformAnonymous([last.vessel])[0];
            this.landing.returnPort = last.returnPort;
            if (last.tickets.length > 0) this.lastBuyer = last.tickets[last.tickets.length - 1].buyer;
          }

          //Only include sets with Pelagic or longline gear
          this.landing.sets = activeTrip.sets.filter(st => st.fishery == 'Groundfish' && 
            (((st.gear?.type ?? '') == 'Longline' || (st.gear?.type ?? '') == 'Pelagic') ||
            ((st.gear?.type ?? '') == 'Pot' && 
            st.endDate && 
            !st.endLat.isUnfilled() && 
            !st.endLon.isUnfilled())));

          this.landing.isNewDelivery = false;
          this.landing.operatorName = `${this.dataSync.current_user().firstName} ${this.dataSync.current_user().lastName}`;
          this.dataSync.user_trip.update(trip => {
            trip.groundfishDelivery = this.landing;
            return trip; 
          });
          this.dataSync.updateLanding(Fishery.Groundfish, false).then(result => {
            if (result.success) console.log("New delivery updated"); 
            else {
              this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
              this.insights.trackException(result.error);
            }
          });        
        }
        break;
      case DeliveryMode.Completed:
        this.landing = this.dataSync.received_logbooks()[this.completedInd] as GroundfishDelivery;

        this.canEdit = editWindowExpiry(this.landing) > new Date();
        this.vesselOptions.set(VesselData.transformAnonymous([this.landing.vessel]));
        this.landingForm.disable();
        this.permitNumControl.disable();
        break;
      case DeliveryMode.EditCompleted:
        this.landing = this.dataSync.edited_groundfish();
        if (editWindowExpiry(this.landing) < new Date()) {
          this.landing = this.dataSync.received_logbooks()[this.completedInd] as GroundfishDelivery;
          this.discardEdits(false);
          const windowText = this.landing.isEMTrip ? 'Ninety-day' : 'One week';
          this.toastr.warning(`${windowText} edit time window for this delivery has passed. Contact FedElog@psmfc.org if you have any questions`, 'Logbook edits removed', {timeOut: 8000});
        } else this.canEdit = true;
        break;
    }

    this.patchForm();

    if (this.viewMode != DeliveryMode.Completed) {      
      this.subscribeToFormUpdate();
      this.resetErrorContent();
      this.filteredPorts = this.landingForm.get('landingPort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );
    }

    this.portSubscription = this.lookupService.getPortsObservable().subscribe((value) => this.portOptions = value);     
    this.permitSubscription = this.lookupService.getPermitTypeObservable().subscribe((value) => this.permitTypeOptions = value);
    this.sortSetsByDate();  
  }


  patchForm(emitEvent: boolean = true) {
    this.landingForm.patchValue({
      vessel: this.landing.vessel,
      operator: this.landing.operatorName,
      deliverDate: this.landing.returnDate ? formatDate(this.landing.returnDate, 'fullDate', 'en-US') : '',
      landingPort: this.landing.returnPort.name ? this.landing.returnPort : '',
      permitType: this.landing.permitType,
      isExemptedFP: this.landing.isEFP,
      isEMTrip: this.landing.isEMTrip,
      isObservedTrip: this.landing.isObservedTrip
    }, {emitEvent: emitEvent});
    this.permitNumControl.setValue(this.landing.permitNumber, {emitEvent: emitEvent});
  }

  /**
   * Sort the sets by date first then by their set numbers.
   */
  sortSetsByDate(): void {

    var compareSetNumbers = (a: string, b: string) => { return !b ? -1 : !a ? 1 : a.localeCompare(b); }
    var compareSetDates = (aString: string, bString: string, aNumber: string, bNumber: string) => {
      return (aString < bString) ? -1 : (aString > bString) ? 1 : compareSetNumbers(aNumber, bNumber); }

    this.landing.sets.sort((a, b) => {

      // start sorting, check if dates are null first
      return (a.startDate && !b.startDate) ? -1 : 
      (!a.startDate && b.startDate) ? 1 : 
      (!a.startDate && !b.startDate) ? compareSetNumbers(a.setNumber, b.setNumber) : 
      compareSetDates(new Date(a.startDate).toISOString(), new Date(b.startDate).toISOString(), a.setNumber, b.setNumber); 
    });

    this.sortedSets = this.landing.sets;
  }

/**
 * We update the landing in the database and the current landing object on every form update. 
 */
  subscribeToFormUpdate() {
    this.landingFormSubscription = this.landingForm.valueChanges.subscribe(value => {
      if (!value.vessel) {
        value.vessel = this.landing.vessel;
        this.landingForm.get('vessel')!.setValue(value.vessel, {onlySelf: true, emitEvent: false});
      }
      (this.landing as GroundfishDelivery).updateValue(value);
      if (this.viewMode == DeliveryMode.Current) {
        this.dataSync.user_trip.update(trip => {
          trip.groundfishDelivery = this.landing;
          return trip; 
        });
      } else if (this.viewMode == DeliveryMode.EditCompleted) {
        this.dataSync.edited_logbook.update(el => {
          el.GF = this.landing;
          return {...el, GF: this.landing};
        })
      }
      
      this.dataSync.updateLanding(Fishery.Groundfish, this.viewMode == DeliveryMode.EditCompleted).then(result => {
        if (result.success) {
          if (value.permitType == 'Open Access' && (this.permitNumControl.value as string).length != 6) this.permitNumControl.reset('');
          else if (this.permitNumControl.disabled && value.permitType != 'Open Access') this.permitNumControl.enable();
          
          if (value.permitType != 'IFQ') this.landingForm.get('isEMTrip')?.setValue(false, { emitEvent: false });
          this.resetErrorContent();
        } else {
          this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
          this.insights.trackException(result.error);
        }      
      })      
    });

    //Separate subscription for permit number since it may be enabled/disabled 
    this.permitNumSubscription = this.permitNumControl.valueChanges.subscribe(value => {
      if (this.permitNumControl.enabled) {
        this.landing.permitNumber = value ? value : '';
        this.dataSync.updateLanding(Fishery.Groundfish, this.viewMode == DeliveryMode.EditCompleted).then(result => {
          if (result.success) this.resetErrorContent();
          else {
            this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
            this.insights.trackException(result.error);
          }
        });
      }
    });
  }


  compareFn(l1: LookupItem, l2: LookupItem): boolean {
    return l1 && l2 ? l1.code === l2.code : l1 === l2;
  }

  compareVessel(v1: VesselData, v2: VesselData): boolean {
    return v1 && v2 ? v1.vesselName+v1.vesselCoastNumber === v2.vesselName+v2.vesselCoastNumber : v1 === v2;
  }

  /**
   * Go get all errors/warnings for the delivery and its sets
   */
  resetErrorContent() {
    this.errors = this.tripValidator.errorWarningDialogContentGroundfish(this.viewMode == DeliveryMode.EditCompleted);
    this.onlyWarnings = !this.errors.some(x => x.isError);
    this.validSets.clear();
    this.preExistingSets.clear();
    let allSets = this.dataSync.user_trip().sets;
    this.landing.sets.forEach(set => {
      this.validSets.set(set.fishingEventID, this.errors.find(x => x.setID == set.fishingEventID && x.isError) == undefined ? true : false);
      this.preExistingSets.set(set.fishingEventID, allSets.findIndex((fe: FishingEvent) => fe.fishingEventID == set.fishingEventID) == -1);
    });
  }


  /**
   * As in settings we can add a new vessel here.
   */
  addVessel() {
    this.common.addVessel(-1, false).then((newVessel: VesselData) => this.landingForm.get('vessel')?.setValue(newVessel))
    .catch((err: any) => console.log(err))
  }


  /**
   * Display errors/warnings to the user if there are any. Otherwise we go to the summary-review component.
   */
  openSubmitDialog() {   
    if (this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE) {
      this.dialog.open(SubmitDialogComponent, {
        autoFocus: false,
        width: '400px',
        height: '450px',
        data: this.errors.filter(x => x.isError).length > 0
      });

    } else {
      this.openErrorDialog(true);
    }    
  }


  editDateTime() {
    if (this.viewMode == DeliveryMode.Completed) return;
    let dateInput = this.landingForm.get('deliverDate')?.value == '' ? new Date() : new Date(this.landingForm.get('deliverDate')?.value);

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      data: {date: dateInput, isDelivery: true, isEnd: true},
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) this.landingForm.get('deliverDate')?.setValue(formatDate(result, 'fullDate', 'en-US'));
    });
  }


  /**
   * Adding ar editing a fishing ticket
   * @param index Index into landing tickets array, if we are editing an existing ticket
   * @returns 
   */
  openFishTicketDialog(index: number) {
    if (this.viewMode == DeliveryMode.Completed) return;
    if (index >= this.landing.tickets.length) {
      this.landing.tickets.push(new FishTicket('', ''));
      this.landing.tickets[index].deliveryDate = this.landingForm.get('deliverDate')?.value == '' ? new Date() : new Date(this.landingForm.get('deliverDate')?.value);
      this.landing.tickets[index].buyer = this.lastBuyer;
    }

    let usedNumbers: string[] = [];
    this.landing.tickets.forEach(tck => {if (this.landing.tickets[index].ticketNumber != tck.ticketNumber) usedNumbers.push(tck.ticketNumber)});

    const dialogRef = this.dialog.open(AddTicketDialogComponent, {
      autoFocus: false,
      width: '400px',
      position: {top: '15px'},
      disableClose: true,
      data: {tckt: this.landing.tickets[index], usedNums: usedNumbers, returnDate: this.landing.returnDate}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.delete) {
          this.landing.tickets.splice(index, 1);
        } else {
          if (index < this.landing.tickets.length) {
            this.landing.tickets[index].buyer = result.buyer;
            this.landing.tickets[index].ticketNumber = result.number;
            this.landing.tickets[index].deliveryDate = result.date;
          } else {
            this.landing.tickets.push(new FishTicket('', ''));
            this.landing.tickets[index].buyer = result.buyer;
            this.landing.tickets[index].ticketNumber = result.number;
            this.landing.tickets[index].deliveryDate = result.date;
          }
        }
        this.table.renderRows();
        this.dataSync.updateLanding(Fishery.Groundfish, this.viewMode == DeliveryMode.EditCompleted).then(result => {
          if (!result.success) {
            this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
            this.insights.trackException(result.error);
          }
        });
      } else {
        this.landing.tickets.splice(index, 1);
      }
      this.resetErrorContent();
    });
  }


  /**
   * Adding a new permit number
   * @returns 
   */
  openPermitDialog() {
    if (this.viewMode == DeliveryMode.Completed) return;

    // Retrieve the current permit type from the landingForm.
    const permitType = this.landingForm.get('permitType')?.value?.name;

    const dialogRef = this.dialog.open(PermitNumDialogComponent, {
      width: '300px',
      // Pass along the permit type to the dialog.
      data: { permitType }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dataSync.addAuxiliaryData(result, 'permit_numbers').then(res => {
          if (res.success) {
            this.landing.permitNumber = result;
            this.permitNumControl.setValue(result);
          } else {
            this.toastr.error('Permit number already exists', 'Error', {timeOut: 3000});
            this.insights.trackException(res.error);
          }
          
        });
      } else {
        this.permitNumControl.setValue(this.landing.permitNumber);
      }
    });
  }

  get filteredPermitNums(): string[] {
    const permitType = this.landingForm.get('permitType')?.value?.name;
    if (permitType === 'Open Access') {
      // Only return permit numbers with 6 digits for Open Access
      return this.dataSync.permit_nums().filter(num => num.length === 6);
    } else {
      // For IFQ and LE, only return permit numbers that are 4 digits
      return this.dataSync.permit_nums().filter(num => num.length === 4);
    }
  }




  /**
   * For adding/removing sets attached to the delivery
   * @returns 
   */
  openSetSelection() {
    //Editing?
    if (this.viewMode == DeliveryMode.Completed) return;
    let ids: string[] = this.landing.sets.map(s => s.fishingEventID);
    const dialogRef = this.dialog.open(SetSelectionDialogComponent, {
      width: '400px',
      height: '600px',
      data: {
        data: ids,
        editedDelivery: this.viewMode == DeliveryMode.EditCompleted,
        fishery: 'Groundfish'
      }
    });

    dialogRef.afterClosed().subscribe((result: FishingEvent[]) => {
      if (result) {
        this.landing.sets = this.viewMode == DeliveryMode.EditCompleted ? 
                  this.landing.sets.filter((fe: FishingEvent) => this.preExistingSets.get(fe.fishingEventID)).concat(result) : result;
        this.dataSync.updateLanding(Fishery.Groundfish, this.viewMode == DeliveryMode.EditCompleted).then(res => {
          if (!res.success) {
            this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
            this.insights.trackException(res.error);
          } else {
            this.resetErrorContent();
            this.sortSetsByDate();
          }
        });       
      }
    });
  }


  /**
   * Clicking on a set attached to delivery takes us to that set
   * @param setID fishingEventID of set to go to
   * @returns 
   */
  goToSet(setID: string) {
    //Editing?
    if (this.viewMode == DeliveryMode.Completed) {
      // Find the set index from the landing sets.
      const setIndex = this.landing.sets.findIndex(x => x.fishingEventID === setID);
      if (setIndex !== -1) {
        // Navigate to the set page and pass a parameter that indicates "readOnly" mode.
        this.router.navigate(['set_detail', setIndex, false], { queryParams: { previous: 'deliveries_list', completedIndex: this.completedInd, readOnly: true } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) {
          window.scrollTo(0, 0);
        }
      }
      return;
    }
    let setIndex = this.dataSync.user_trip().sets.findIndex(x => x.fishingEventID === setID);
    if (setIndex != -1) {
      this.router.navigate(['set_detail', setIndex, false], { queryParams: { previous: 'delivery'} });
      if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
    } else if (this.viewMode == DeliveryMode.EditCompleted) {
      //The set no longer exists on device and is saved as part of this delivery rather than the user sets
      this.router.navigate(['set_detail', -1, false], { queryParams: { editedIndex: this.landing.sets.findIndex(x => x.fishingEventID === setID) } });
      if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
    }   

  }


  removeSet(index: number) {
    //Editing?
    this.landing.sets.splice(index, 1);
    this.dataSync.updateLanding(Fishery.Groundfish, this.viewMode == DeliveryMode.EditCompleted).then(res => {
      if (!res.success) {
        this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
        this.insights.trackException(res.error);
      }
      this.resetErrorContent()
    });
  }


  /**
   * Get the errors/warnings from {@link TripValidator.errorWarningDialogContent} and put it in the opened dialog
   */
  openErrorDialog(submission: boolean = false) {
    this.resetErrorContent();

    if (this.errors.length == 0 && submission) {
      console.log('Going to summary review')
      this.router.navigate(['summary_review', 'Groundfish'], { queryParams: { previous: this.previousScreen, deliveryIndex: this.viewMode == DeliveryMode.EditCompleted ? this.completedInd : -1 } });
      return;
    }

    //Launch dialog with collected results
    const dialogRef = this.dialog.open(ErrorWarningDialogComponent, {
      autoFocus: false,
      width:  (Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) - 10).toString() + 'px',
      height: (Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) - 10).toString() + 'px',
      data: {errors: this.errors, submission: submission}
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result != undefined) {
        if (submission && result == -2) {
          //Go to summary review
          console.log('Going to summary review')
          this.router.navigate(['summary_review', 'Groundfish'], { queryParams: { previous: this.previousScreen, deliveryIndex: this.viewMode == DeliveryMode.EditCompleted ? this.completedInd : -1} });
        } else if (result != -1) {
          if (this.viewMode == DeliveryMode.EditCompleted) {
            let setInd = this.dataSync.edited_groundfish().sets.findIndex(s => s.fishingEventID == result);
            this.router.navigate(['set_detail', -1, false], { queryParams: { editedIndex: setInd } });
          } else {
            let setInd = this.dataSync.user_trip().sets.findIndex(s => s.fishingEventID == result);
            this.router.navigate(['set_detail', setInd, false], { queryParams: { previous: 'delivery' } });
          }
          if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
        }
      }
    });
  }


  enableEdits() {
    //Check for edits in progress on another delivery
    let logbook = this.dataSync.edited_groundfish().logbookId ? this.dataSync.edited_groundfish() : this.dataSync.edited_hms();
    if (logbook.logbookId && logbook.logbookId != this.landing.logbookId) {
      this.toastr.warning(`Logbook ${logbook.logbookId.substring(17)} has unsubmitted edits. Submit or discard those to edit this logbook`,
      'Multiple edited logbooks');
      return;
    }

    if (this.landing.isEMTrip) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {screen: 'emedit'}
      }).afterClosed().subscribe(result => {
        if (result) this.beginEdits();
      });
    } else {
      this.beginEdits();
    }
  }


  private beginEdits() {
    if (this.updateEffect) this.updateEffect.destroy();
    this.dataSync.editExistingGroundfishLanding(this.landing.logbookId).then(res => {
      if (!res.success) {
        this.toastr.error('Error editing delivery', 'Error', {timeOut: 3000});
        this.insights.trackException(res.error);
        return;
      }

      this.viewMode = DeliveryMode.EditCompleted;
      this.router.navigate(['delivery_detail', 2, this.completedInd], { queryParams: { previous: 'deliveries_list' } });
      this.landing = this.dataSync.edited_groundfish();
      
      this.landingForm.enable();
      if (this.landing.permitType.name != 'Open Access') this.permitNumControl.enable();

      this.subscribeToFormUpdate();
      this.resetErrorContent();
      this.filteredPorts = this.landingForm.get('landingPort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );

      this.updateEffect = effect(() => {
        this.landing = this.dataSync.edited_groundfish();
        this.patchForm();
        this.sortSetsByDate();
        this.table.renderRows();
      }, {injector: this.injector});
    })
    
  }


  discardEdits(promptUser: boolean) {
    if (this.updateEffect) this.updateEffect.destroy();
    //Lots of warnings and user confirmation before going forward
    let discard = () => {
      this.viewMode = DeliveryMode.Completed;

      this.landingSubscription?.unsubscribe(); 
      this.landingFormSubscription?.unsubscribe();
      this.permitSubscription?.unsubscribe();
      this.landingForm.disable();
      this.permitNumControl.disable();

      this.dataSync.discardLogbookEdits();

      //Unassign LocalStorageService.edited_landing, revert this landing to completed delivery landing
      this.landing = this.dataSync.received_logbooks()[this.completedInd] as GroundfishDelivery;
      this.vesselOptions.set(VesselData.transformAnonymous([this.dataSync.received_logbooks()[this.completedInd].vessel]));
      this.landingForm.patchValue({
        vessel: this.dataSync.received_logbooks()[this.completedInd].vessel,
        operator: this.dataSync.received_logbooks()[this.completedInd].operatorName,
        deliverDate: this.landing.returnDate ? formatDate(this.landing.returnDate, 'fullDate', 'en-US') : '',
        landingPort: this.landing.returnPort.name ? this.landing.returnPort : '',
        permitType: this.landing.permitType,
        isExemptedFP: this.landing.isEFP,
        isEMTrip: this.landing.isEMTrip,
        isObservedTrip: this.landing.isObservedTrip
      });
      this.permitNumControl.setValue(this.landing.permitNumber);
      if (this.landing.permitType.name === 'Open Access') this.permitNumControl.disable();
      this.sortSetsByDate();
    };

    if (promptUser) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {screen: 'discardedits'}
      }).afterClosed().subscribe(result => {
        if (!result) return;
        discard();  
      });  
    } else discard();
    
  }


  navigateBack() {
    if (this.previousScreen == 'set_detail') this.router.navigate(['set_detail', this.previousSetIndex, false]);
    else this.router.navigate([this.previousScreen]);
  }


  ngOnDestroy() {
    if (this.viewMode != DeliveryMode.Completed) {
      if (this.landingFormSubscription) this.landingFormSubscription.unsubscribe();
      if (this.portSubscription) this.portSubscription.unsubscribe();
      if (this.permitSubscription) this.permitSubscription.unsubscribe();
      if (this.landingSubscription) this.landingSubscription.unsubscribe(); 
    }   
  }
}
